import { useEffect, useState } from "react";

// Anti-copy, anti-print, anti-screenshot best-effort protection.
// Browsers cannot fully block OS-level screenshots, so we combine:
//  • right-click / copy / cut / print / save / view-source shortcuts blocked
//  • PrintScreen key intercept + short blur burst
//  • visibilitychange & window blur → full blur overlay (defeats casual screen capture apps)
//  • CSS user-select disabled + drag disabled below
export default function AntiCopyGuard({ children }) {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const onCtx = (e) => e.preventDefault();
    const onCopy = (e) => { e.preventDefault(); };
    const onDrag = (e) => e.preventDefault();
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      // Ctrl/Cmd + S/P/C/X/U/A blocked
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c", "x", "u", "a"].includes(k)) e.preventDefault();
      // PrintScreen — clear clipboard then blur briefly
      if (k === "printscreen") {
        e.preventDefault();
        try { navigator.clipboard?.writeText("Screenshots of this confidential SOP are prohibited."); } catch (_) {}
        setHidden(true);
        setTimeout(() => setHidden(false), 1500);
      }
    };
    const onVis = () => setHidden(document.hidden);
    const onBlur = () => setHidden(true);
    const onFocus = () => setHidden(false);
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);
    document.addEventListener("dragstart", onDrag);
    document.addEventListener("keydown", onKey);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
      document.removeEventListener("dragstart", onDrag);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return (
    <div className="no-select">
      {children}
      {hidden && (
        <div className="blur-shield" data-testid="blur-shield">
          <div className="text-center px-6">
            <div className="font-display text-3xl mb-2" style={{ color: "var(--brand-amber)" }}>Content Hidden</div>
            <div className="text-sm">Return to this window to continue.</div>
            <div className="mt-3 text-[10px] tracking-[0.3em] uppercase" style={{ color: "var(--brand-amber)", opacity: 0.7 }}>
              Screenshots &amp; screen recording are prohibited under the Franchise Agreement.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
